import React, { useEffect, useMemo, useState, useRef } from "react";
import MaterialTable, { MTableToolbar, MTablePagination } from "material-table";
import SyncIcon from "@material-ui/icons/Sync";
import { Button, Grid, Card, CardContent, CircularProgress, Typography, TextField } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { waymarkerApi } from "../../api/waymarker";

const formatWaymarker = (wm) => {
    return {
        waymarker_id: wm.id,
        waymarker_type: wm.type ?? "",
        created_at: wm.created_at ?? "",
        race_name: wm.race_name ?? "",
        race_stage_index: wm.race_stage_index ?? "",
        distance_to_finish: wm.distance_to_finish ?? "",
        description: wm.description ?? "",
        url: wm.url ?? "",
        race_stage_id: wm.race_stage_id ?? null,
        incident_id: wm.incident_id ?? null,
    };
};

export default function WaymarkerOverview() {
    const history = useHistory();
    const [stageId, setStageId] = useState("");
    const [waymarkers, setWaymarkers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSync, setLoadingSync] = useState(false);
    const [message, setMessage] = useState("");

    const handleForceSync = async () => {
        setLoadingSync(true);
        await waymarkerApi.syncWaymarkers();
        setLoadingSync(false);
        window.location.reload();
    }

    const fetchWaymarkers = async () => {
        try {
            setLoading(true);
            setMessage("");
            const data = await waymarkerApi.getWaymarkers(stageId);
            const list = Array.isArray(data) ? data.map(formatWaymarker) : [];
            setWaymarkers(list);
            if (!Array.isArray(data)) {
                setMessage("Unexpected response from server - expected array.");
            }
        } catch (err) {
            console.error("Error fetching waymarkers", err);
            setMessage("Failed to load waymarkers.");
            setWaymarkers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWaymarkers();
    }, []);

    useEffect(() => {
        fetchWaymarkers();
    }, [stageId]);

    const tableData = useMemo(() => waymarkers, [waymarkers]);

    return (
        <div key="waymarker-overview">
            <h1>Waymarkers Overview</h1>
            {/*
            <Grid container spacing={2} alignItems="flex-end" style={{ marginBottom: 12 }}>
                <Grid item>
                    <TextField
                        label="Race Stage ID"
                        value={stageId}
                        onChange={(e) => setStageId(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item>
                    <Button variant="contained" color="primary" onClick={fetchWaymarkers} disabled={loading || !stageId}>
                        {loading ? "Loading..." : "Refresh"}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="default"
                        onClick={() => setWaymarkers((prev) => [...prev])}
                        disabled={loading}
                    >
                        Re-render
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    {message && <Typography color="error">{message}</Typography>}
                </Grid>
            </Grid>
            */}

            <MaterialTable
                title={null}
                columns={[
                    //{ title: "ID", field: "waymarker_id", type: "numeric" },  
                    { title: "Created At", field: "created_at", render: (rowData) => new Date(rowData.created_at).toLocaleString(),
                        customFilterAndSearch: (search, rowData) => {
                            if (!rowData.created_at) return false;
                            const createdAtString = new Date(rowData.created_at).toLocaleString();
                            return createdAtString.toLowerCase().includes(search.toLowerCase());
                        }
                    },
                    { title: "Type", field: "waymarker_type" },
                    { title: "Race", field: "race_name" },
                    { title: "Stage", field: "race_stage_index", type: "numeric" },
                    { title: "Distance to finish (km)", field: "distance_to_finish", type: "numeric" },
                    { title: "Description", field: "description" },
                    {
                        title: "Links",
                        field: "links",
                        render: (rowData) => (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                    component="a"
                                    href={rowData.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    URL
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => { if (rowData.race_stage_id) window.open(`/#/race/${rowData.race_stage_id}`, '_blank'); }}
                                    disabled={!rowData.race_stage_id}
                                >
                                    R {rowData.race_stage_id ?? "-"}
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => { if (rowData.incident_id) window.open(`/#/incident/${rowData.incident_id}`, '_blank'); }}
                                    disabled={!rowData.incident_id}
                                >
                                    I {rowData.incident_id ?? "-"}
                                </Button>
                            </div>
                        ),
                    },
                ]}
                data={tableData}
                options={{
                    sorting: true,
                    filtering: true,
                    search: false,
                    paging: true,
                    pageSize: 50,
                    pageSizeOptions: [50, 100],
                    exportButton: false,
                    actionsColumnIndex: -1,
                }}
                actions={[
                    {
                        icon: () =>
                            loadingSync ? (
                                <CircularProgress size={20} />
                            ) : (
                                <SyncIcon />
                            ),
                        tooltip: "Force Sync waymarkers",
                        isFreeAction: true, // puts button in toolbar
                        onClick: handleForceSync,
                    },
                ]}
                components={{
                    Toolbar: (props) => {
                        const count = props.dataManager?.filteredData.length ?? 0;
                        return (
                            <>
                                <MTableToolbar {...props} />
                                <div style={{ marginTop: -50, padding: "8px 16px" }}>
                                    <strong>Search result contains {count} waymarkers</strong>
                                </div>
                            </>
                        )
                    },
                }}
            />
        </div>
    );
}
